import {
  TempTransitionsEntityKind,
  TransitionsEntityKind,
  type TransitionsFiltersV2,
  type TransitionsReqEntV2,
  type TransitionsReqV2,
} from '@revelio/data-access';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../shared/selection-lists/types';
import { DEFAULT_SKILL_TAXONOMY_ID } from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getCustomRoleFilter } from './getCustomRoleFilter';
import { getCustomSkillFilter } from './getCustomSkillFilter';
import { getDateFromFilters } from './getDateFilter';
import { getListFilter } from './getListFilter';
import { getStaticListBoolean } from './getStaticListBoolean';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

const ENTITY_TYPE_MAPPING: Partial<
  Record<AllSelectionListsWithSkillTaxonomyKey, TransitionsEntityKind>
> = {
  rics_k10: TransitionsEntityKind.RicsK10,
  rics_k400: TransitionsEntityKind.RicsK400,
  company: TransitionsEntityKind.Company,
  region: TransitionsEntityKind.Region,
  country: TransitionsEntityKind.Country,
  msa: TransitionsEntityKind.Msa,
  state: TransitionsEntityKind.State,
  city: TransitionsEntityKind.City,
  role_k7: TransitionsEntityKind.RoleK7,
  role_k150: TransitionsEntityKind.RoleK150,
  role_k1500: TransitionsEntityKind.RoleK1500,

  // TODO: Replace with TransitionsEntityKind when available
  role_k15000:
    TempTransitionsEntityKind.RoleK15000 as unknown as TransitionsEntityKind,
};

export const TransitionDateGranularity = 'month' as const;

const extractNumericId = (id: string): number => {
  const parts = id.split('_');
  if (parts.length > 1) {
    return parseInt(parts[parts.length - 1] ?? '', 10);
  }
  return parseInt(id ?? '', 10);
};

export const getTransitionV2Filters = (
  effectiveFilters: Filter[],
  primaryDimension: PrimaryFilterId
): TransitionsReqV2 | null => {
  const primaryFilter = effectiveFilters.find(
    (filter) => filter.id === primaryDimension
  );

  let primaryEntities: TransitionsReqEntV2[] = [];
  if (primaryFilter && primaryFilter.type === FilterType.TREE) {
    const treeFilter = primaryFilter;
    primaryEntities = treeFilter.selections
      .filter((selection) => !!selection.id)
      .map((selection) => {
        const EntType = ENTITY_TYPE_MAPPING[selection.levelId];
        if (!EntType) return null;
        return { EntID: extractNumericId(selection.id), EntType };
      })
      .filter((entity) => entity !== null);
  }

  if (primaryEntities.length === 0) {
    return null;
  }

  const serializedFilters = effectiveFilters.reduce<TransitionsFiltersV2>(
    (acc, filter): TransitionsFiltersV2 => {
      if (filter.id === primaryDimension) {
        return acc;
      }

      const getTransitionListFilter = (key: keyof TransitionsFiltersV2) => {
        if (filter.type !== FilterType.LIST) return acc;
        return {
          ...acc,
          ...getListFilter<TransitionsFiltersV2>(filter, key),
        };
      };

      return ((): TransitionsFiltersV2 => {
        switch (filter.id) {
          case FilterId.COMPANY:
            return { ...acc, ...getV2CompanyFilters(filter) };

          case FilterId.ROLE_TAXONOMY: {
            if (filter.type !== FilterType.VALUE || !filter.value) return acc;
            return {
              ...acc,
              custom_role: {
                ...acc.custom_role,
                taxonomyId: stripVersionFromTaxonomyId(filter.value),
              },
            };
          }
          case FilterId.ROLE:
            return { ...acc, ...getCustomRoleFilter(filter, effectiveFilters) };

          case FilterId.GEOGRAPHY:
            return { ...acc, ...getV2GeographyFilters(filter) };

          case FilterId.GENDER:
            return getTransitionListFilter('gender');

          case FilterId.ETHNICITY:
            return getTransitionListFilter('ethnicity');

          case FilterId.EDUCATION:
            return getTransitionListFilter('highest_degree');

          case FilterId.SENIORITY:
            return getTransitionListFilter('seniority');

          case FilterId.TITLE: {
            if (filter.type === FilterType.FREE_TEXT) {
              return { ...acc, raw_title: [filter.text] };
            }
            return acc;
          }
          case FilterId.KEYWORDS: {
            if (filter.type === FilterType.FREE_TEXT) {
              return { ...acc, keywords: [filter.text] };
            }
            return acc;
          }

          case FilterId.CUSTOM_SKILL:
            return {
              ...acc,
              ...getCustomSkillFilter(filter, effectiveFilters),
            };

          case FilterId.SKILL_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const taxonomyId =
                (filter.value as { taxonomyId?: number })?.taxonomyId ??
                DEFAULT_SKILL_TAXONOMY_ID;
              return {
                ...acc,
                custom_skill: {
                  ...(acc.custom_skill || {}),
                  taxonomy_id: taxonomyId,
                },
              } as TransitionsFiltersV2;
            }
            return acc;
          }

          case FilterId.CONTINGENT_WORKERS:
            return {
              ...acc,
              ...getStaticListBoolean(filter, 'contingent_workers'),
            };

          case FilterId.DATE_RANGE:
          case FilterId.SNAPSHOT_DATE:
          case FilterId.OVERALL_RATING:
          case FilterId.POSTINGS_SOURCE:
          case FilterId.DATA_ADJUSTMENTS:
            return acc;
          default:
            return acc;
        }
      })();
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters: effectiveFilters,
    granularity: TransitionDateGranularity,
  });

  const serializedFiltersWithDate = {
    ...serializedFilters,
    ...dateFilter,
  };

  if (primaryEntities.length === 0) {
    return null;
  }

  const result: TransitionsReqV2 = {
    entities: primaryEntities,
    filters: serializedFiltersWithDate,
  };

  return result;
};
